import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/common/tabs";
import LoadingOverlay from "../ingredientList/components/LoadingOverlay";
import { IngredientRowViewModel } from "../ingredientList/types/ingredientListView.types";
import CalculateConfirmDialog from "./components/CalculateConfirmDialog";
import IngredientDetailsFooter from "./components/IngredientDetailsFooter";
import IngredientDetailsHeader from "./components/IngredientDetailsHeader";
import IngredientUnitBadge from "./components/IngredientUnitBadge";
import EnvironmentalFootprintTab from "./components/tabs/EnvironmentalFootprintTab";
import HistoryTab from "./components/tabs/HistoryTab";
import SummaryTab from "./components/tabs/SummaryTab";
import VolumeAndCostTab from "./components/tabs/VolumeAndCostTab";
import { useIngredientDetailsViewModel } from "./hooks/useIngredientDetailsViewModel";

interface IngredientDetailsModalProps {
  ingredientDetails: IngredientRowViewModel | undefined;
  visibleModal: boolean;
  setVisibleModal: (visible: boolean) => void;
  navigateIngredientEdit: (visible: boolean) => void;
}

const TABS = [
  { key: "0", label: "Ingredient Summary" },
  { key: "1", label: "Environmental Footprint" },
  // Supply Chain Mapping (key "2") and Supplier Ingredients (key "5") tabs are
  // hidden per product request; keys are kept as-is (not renumbered) so
  // `setSelectedTab` calls elsewhere (e.g. jumping to Environmental Footprint)
  // keep pointing at the right tab.
  { key: "3", label: "History" },
  { key: "4", label: "Volume and Cost" }
];

/**
 * Tailwind rebuild of the legacy `IngredientDetailsModal`
 * (src/screens/ingredients/ingredientDetails/IngredientDetailsModal.tsx) —
 * same read-only summary/impact/history/volume-and-cost layout (the Supply
 * Chain Mapping and Supplier Ingredients tabs are currently hidden), off
 * Semantic UI (`Grid`/`TabView`/`Dimmer`) and onto the `TailwindSideModal`
 * pattern used by the register/edit modals, with `common/tabs.tsx` (Radix)
 * for tab navigation.
 *
 * Header matches the Lovable prototype: `eyebrow`/`titleClassName`/
 * `titleMeta`/`headerRight` (type + data-type chips, right-aligned unit).
 * Body is a plain flex layout rather than `CommonSplitContainer` — that
 * component sizes its scroll area as `windowHeight * currentHeight - 5rem`,
 * a fixed allowance that assumed a one-line header; this header is now
 * three lines tall, so a flex column (`SheetContent` is already
 * `flex flex-col h-full`) is used instead, same as `ProductDetailsModal`.
 * This also lets the tab bar be `tw-sticky` while the tab content scrolls
 * beneath it.
 *
 * Drop-in replacement wherever the legacy modal is rendered for the details
 * flow (same props, `ingredientDetails` narrowed to the typed
 * `IngredientRowViewModel` the Tailwind list already produces).
 */
const IngredientDetailsModal = ({
  ingredientDetails,
  visibleModal,
  setVisibleModal,
  navigateIngredientEdit
}: IngredientDetailsModalProps) => {
  const vm = useIngredientDetailsViewModel({
    ingredientDetails,
    setVisibleModal,
    navigateIngredientEdit
  });

  return (
    <>
      <TailwindSideModal
        open={visibleModal}
        onOpenChange={open => {
          if (!open) vm.onCancel();
        }}
        eyebrow="Ingredient"
        title={vm.ingredientDetails?.ingredient_name || "Ingredient Details"}
        titleClassName="tw-font-display tw-text-2xl tw-truncate"
        titleMeta={<IngredientDetailsHeader vm={vm} />}
        headerRight={<IngredientUnitBadge vm={vm} />}
        onOpenAutoFocus={e => e.preventDefault()}
        contentClassName="tw-bg-[#FBFAF7]"
      >
        <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col">
          <Tabs
            value={String(vm.selectedTab)}
            onValueChange={value => vm.setSelectedTab(Number(value))}
            className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col"
          >
            <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden">
              <TabsList className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-h-auto tw-w-full tw-flex-wrap tw-justify-start tw-gap-6 tw-rounded-none tw-border-b tw-border-border tw-bg-[#FBFAF7] tw-p-0 tw-px-6">
                {TABS.map(tab => (
                  <TabsTrigger
                    key={tab.key}
                    value={tab.key}
                    className="tw--mb-px tw-rounded-none tw-border-b-2 tw-border-transparent tw-bg-transparent tw-px-1 tw-py-3 tw-text-sm tw-font-medium tw-text-muted-foreground tw-shadow-none data-[state=active]:tw-border-primary data-[state=active]:tw-bg-transparent data-[state=active]:tw-text-primary data-[state=active]:tw-shadow-none"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="tw-px-6 tw-py-3">
                <TabsContent value="0">
                  <SummaryTab ingredientDetails={ingredientDetails} />
                </TabsContent>
                <TabsContent value="1">
                  <EnvironmentalFootprintTab ingredientDetails={ingredientDetails} />
                </TabsContent>
                <TabsContent value="3">
                  <HistoryTab ingredientDetails={ingredientDetails} />
                </TabsContent>
                <TabsContent value="4">
                  <VolumeAndCostTab ingredientDetails={ingredientDetails} />
                </TabsContent>
              </div>
            </div>
          </Tabs>

          <div className="tw-shrink-0">
            <IngredientDetailsFooter vm={vm} />
          </div>
        </div>
      </TailwindSideModal>

      <CalculateConfirmDialog vm={vm} />

      {vm.isLoadingPutDataToQueue ? <LoadingOverlay /> : null}
    </>
  );
};

export default IngredientDetailsModal;
